// src/components/Inquiry/items/goodsIn.tsx
//
// Goods-in per line, for an inquiry in Pickup / Delivery and later.
//
// A supplier rarely ships a six-piece line in one go, and the inquiry status
// is one value for every line - so "3 of 6 are here" had nowhere to go. Each
// item line gets a Received count, a Ready-to-use count, and - unless the
// goods are being delivered - a Ready-to-pick-up count, as extra cells on the
// row it describes. Typed, not stepped: a pallet of 30 on a line of 100 is one
// number, not thirty clicks.
//
// The counts typed into the table are held locally until the buyer presses
// Save (EE-253): a count written the moment the field lost focus moved the
// status under the buyer while they were still filling the rest in. Save
// sends one PATCH per changed line, carrying every count changed on it; a
// line the backend refuses (over-delivery, a count pushed under the one nested
// inside it) keeps its unsaved numbers and says why, the others go through.
// Cancel puts the table back to what is saved.
//
// The counts are what the inquiry status is read from, from Pickup / Delivery
// up to Ready to Use, so a save can move the status and the dialog re-reads
// the inquiry after it. Arriving is not the same as being ready to use: a QC
// step sits between the two, so "all received" on its own still reads
// Pickup / Delivery. Completed is the buyer's own step on top of that.
//
// useGoodsIn holds the counts for the whole table; GoodsInCells draws one
// line's share of them. Split that way because the table renders several row
// variants, and each of them only has to append the cells.
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { InputAdornment, TableCell, TextField, Typography } from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { InquiryItemType, InquiryType } from "types/InquiryTypes";
import { apiUpdateInquiryItemDelivery } from "util/network/Inquiries";
import { CELL_PRIMARY, ROW_FONT_PRIMARY, SP_1 } from "components/Dashboard/dashboardTable";
import { isInquiryFinished } from "pages/outsourcing_management/constants/inquiryStatusColors";
import { itemCellProps } from "./inquiryItemColumns";
import { InquiryLine } from "./inquiryLine";

type Counts = {
  receivedQty: number;
  readyQty: number;
  readyToUseQty: number;
};

export type GoodsInState = {
  /** The Ready to pick up column - every inquiry but a Delivery one. A
   * courier does not announce readiness, so a delivery line goes straight to
   * Received and the backend refuses a ready count on it. */
  pickup: boolean;
  /** Numbers, no fields, once the inquiry is finished - the record stands. */
  readOnly: boolean;
  /** The counts as shown: the saved ones, with any unsaved edit on top. */
  counts: Record<number, Counts>;
  /** Changes a line's count in the table only - nothing is sent until save. */
  record: (item: InquiryItemType, change: Partial<Counts>) => void;
  /** Something typed that is not saved yet. */
  dirty: boolean;
  saving: boolean;
  /** One PATCH per changed line. */
  save: () => void;
  /** Back to the saved counts. */
  cancel: () => void;
  /** Says why a typed number was not taken. A count that is out of range
   * never reaches the backend, so this is the only thing that can explain it
   * - and a field that silently snaps back to the old number reads as broken
   * rather than as refused. */
  reject: (message: string) => void;
};

// The trailing columns. Kept out of INQUIRY_ITEM_COLUMNS: they exist in one
// state of one mode, and the header only draws them when it is handed a
// GoodsInState.
// In the order a piece travels: the supplier says it is collectable, we
// collect it, someone checks it over. The three are nested running totals -
// ready >= received >= ready to use - not a split of the line quantity, so
// each is shown against the whole line and what is still at the supplier is
// the gap between the first two.
export const GOODS_IN_COLUMNS = {
  ready: {
    label: "Ready to pick up",
    cell: { align: "right" as const, sx: { width: "15%" } },
  },
  received: {
    label: "Received",
    cell: { align: "right" as const, sx: { width: "13%" } },
  },
  readyToUse: {
    label: "Ready to use",
    cell: { align: "right" as const, sx: { width: "14%" } },
  },
};

// The count cells sit side by side, so they take half the horizontal padding
// of a text cell - at laptop width the fields otherwise touch.
export const COUNT_CELL_SX = { padding: SP_1 };

// The four standing columns, re-cut to leave room for the three above. The grid
// is a fixed layout, so widths that add up past 100% are squeezed pro rata -
// which took most of it from Item and Spec, the two that carry text, and left
// Files its full share for two icons. Sized so a count field still fits at
// laptop width (1280), where the table is about 750px. Keyed like
// INQUIRY_ITEM_COLUMNS.
export const GOODS_IN_LAYOUT: Record<"item" | "spec" | "files" | "qty", string> =
  { item: "19%", spec: "25%", files: "7%", qty: "7%" };

// What a line is called in the toast and in the audit row the backend writes:
// the product name, else the requisition spec a converted line carries.
export const lineName = (item: InquiryItemType): string =>
  item.product?.name || item.comment || `Line ${item.id}`;

// Free-text column; the form writes "Pickup" / "Delivery", and a good few
// inquiries reach Pickup / Delivery with no method at all. Only one that says
// Delivery has nothing to be ready for pickup - one that says nothing keeps
// both counts, same rule as the backend.
export const isDeliveryInquiry = (inquiry: { deliveryMethod?: string | null }) =>
  (inquiry.deliveryMethod ?? "").trim().toLowerCase() === "delivery";

// What the received count is called: on a pickup we went and got it, so
// "Picked up" (Olena's validation, 05.10.26); a delivery simply arrived.
export const receivedLabel = (pickup: boolean) =>
  pickup ? "Picked up" : "Received";

const lineCounts = (item: {
  receivedQty?: number;
  readyQty?: number;
  readyToUseQty?: number;
}): Counts => ({
  readyQty: item.readyQty ?? 0,
  receivedQty: item.receivedQty ?? 0,
  readyToUseQty: item.readyToUseQty ?? 0,
});

const COUNT_KEYS: (keyof Counts)[] = ["readyQty", "receivedQty", "readyToUseQty"];

export const useGoodsIn = (
  inquiry: InquiryType,
  /** A save landed; the dialog re-reads the inquiry and the board refreshes. */
  onChanged: () => void
): GoodsInState => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();

  // What the server holds, keyed by line: seeded from the inquiry whenever
  // the dialog re-reads it, and from each PATCH answer as it lands - that one
  // is certainly newer than the write, and carries whatever the backend did on
  // its own (raising Ready to meet Received, say).
  const [saved, setSaved] = useState<Record<number, Counts>>({});
  // The unsaved edits, whole lines keyed the same way. A line is in here
  // only while it differs from `saved`.
  const [edits, setEdits] = useState<Record<number, Counts>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSaved(
      Object.fromEntries(
        (inquiry.items ?? []).map((item) => [item.id, lineCounts(item)])
      )
    );
  }, [inquiry]);

  const counts = { ...saved, ...edits };

  const record = (item: InquiryItemType, change: Partial<Counts>) => {
    const before = counts[item.id];
    if (!before) return;
    const after = { ...before, ...change };
    // Collecting a piece proves it was ready, so Received carries Ready up
    // with it - update_item_delivery does the same thing server-side. Only on
    // a Received change: the backend raises Ready against Received and
    // nothing else, so mirroring it on any other edit puts up a number the
    // save takes straight back down - and every row written under the old
    // rule has Received above Ready.
    if (change.receivedQty !== undefined && !isDeliveryInquiry(inquiry))
      after.readyQty = Math.max(after.readyQty, after.receivedQty);
    const base = saved[item.id];
    setEdits((current) => {
      const next = { ...current };
      if (base && COUNT_KEYS.every((key) => base[key] === after[key]))
        delete next[item.id];
      else next[item.id] = after;
      return next;
    });
  };

  const save = () => {
    const lines = (inquiry.items ?? []).filter((item) => edits[item.id]);
    if (lines.length === 0) return;
    setSaving(true);
    // One line after another, not all at once: each PATCH can move the
    // status, and the backend reads the other lines' counts when it does.
    lines
      .reduce(
        (chain, item) =>
          chain.then(() => {
            const after = edits[item.id];
            const base = saved[item.id] ?? lineCounts(item);
            const change = Object.fromEntries(
              COUNT_KEYS.filter((key) => after[key] !== base[key]).map(
                (key) => [key, after[key]]
              )
            ) as Partial<Counts>;
            return apiUpdateInquiryItemDelivery(token, inquiry.id, item.id, change)
              .then((answer) => {
                const line = answer.items?.find((i) => i.id === item.id);
                if (line)
                  setSaved((c) => ({ ...c, [item.id]: lineCounts(line) }));
                setEdits((c) => {
                  const next = { ...c };
                  delete next[item.id];
                  return next;
                });
              })
              .catch((err) => {
                // The line keeps its unsaved numbers, so the buyer can fix
                // the one that was refused and save again.
                addToast({
                  type: "error",
                  message: `${lineName(item)}: ${err.message ?? err}`,
                  keep: true,
                });
              });
          }),
        Promise.resolve()
      )
      .finally(() => {
        setSaving(false);
        onChanged();
      });
  };

  return {
    pickup: !isDeliveryInquiry(inquiry),
    readOnly: isInquiryFinished(inquiry.state),
    counts,
    record,
    dirty: Object.keys(edits).length > 0,
    saving,
    save,
    cancel: () => setEdits({}),
    reject: (message: string) =>
      addToast({ type: "error", message, keep: true }),
  };
};

/**
 * The goods-in cells of one row: Ready to pick up unless the inquiry is a
 * Delivery, then Received, then Ready to use. Rendered after the Qty cell by
 * every read-mode row.
 */
export const GoodsInCells: FunctionComponent<{
  goodsIn: GoodsInState;
  line: InquiryLine;
}> = ({ goodsIn, line }) => {
  const item = line.item;
  // Only a saved line has counters; the read table never renders another.
  if (!item) return null;
  const quantity = line.quantity ?? 0;
  const counts = goodsIn.counts[item.id] ?? {
    receivedQty: 0,
    readyQty: 0,
    readyToUseQty: 0,
  };
  const complete = quantity > 0 && counts.receivedQty >= quantity;
  const readOnly = goodsIn.readOnly || goodsIn.saving;

  return (
    <>
      {goodsIn.pickup && (
        <TableCell {...itemCellProps(GOODS_IN_COLUMNS.ready, COUNT_CELL_SX)}>
          {/* Everything the supplier has announced, not what is left to
              fetch: it does not fall as pieces are collected, so the gap to
              Received is what is still sitting at their end. */}
          <CountField
            label="Ready to pick up"
            value={counts.readyQty}
            total={quantity}
            max={quantity}
            complete={quantity > 0 && counts.readyQty >= quantity}
            readOnly={readOnly}
            onChange={(next) => goodsIn.record(item, { readyQty: next })}
            onTooHigh={(typed) =>
              goodsIn.reject(
                `${lineName(item)}: ${typed} is more than the ${quantity} on this line.`
              )
            }
          />
        </TableCell>
      )}
      <TableCell {...itemCellProps(GOODS_IN_COLUMNS.received, COUNT_CELL_SX)}>
        <CountField
          label={receivedLabel(goodsIn.pickup)}
          value={counts.receivedQty}
          total={quantity}
          max={quantity}
          complete={complete}
          readOnly={readOnly}
          onChange={(next) => goodsIn.record(item, { receivedQty: next })}
          onTooHigh={(typed) =>
            goodsIn.reject(
              `${lineName(item)}: ${typed} is more than the ${quantity} on this line.`
            )
          }
        />
      </TableCell>
      <TableCell {...itemCellProps(GOODS_IN_COLUMNS.readyToUse, COUNT_CELL_SX)}>
        {/* Capped at what has arrived, not at the line: a piece is checked
            over after it is here, so this count sits on top of Received
            rather than beside it. The backend refuses the same thing. */}
        <CountField
          label="Ready to use"
          value={counts.readyToUseQty}
          total={quantity}
          max={counts.receivedQty}
          complete={quantity > 0 && counts.readyToUseQty >= quantity}
          readOnly={readOnly}
          onChange={(next) => goodsIn.record(item, { readyToUseQty: next })}
          onTooHigh={(typed) =>
            goodsIn.reject(
              `${lineName(item)}: only ${counts.receivedQty} of ${quantity} have been received, so ${typed} cannot be ready to use yet. Record the arrival first.`
            )
          }
        />
      </TableCell>
    </>
  );
};

const CountField: FunctionComponent<{
  label: string;
  value: number;
  total: number;
  max: number;
  complete?: boolean;
  readOnly: boolean;
  onChange: (next: number) => void;
  /** Typed number was over `max`; nothing was sent. */
  onTooHigh?: (typed: number) => void;
}> = ({
  label,
  value,
  total,
  max,
  complete,
  readOnly,
  onChange,
  onTooHigh,
}) => {
  // What is in the field while it is being typed; the count itself lives in
  // useGoodsIn and only moves on commit. Follows the count whenever a save
  // or a cancel changes it under the field.
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = () => {
    const typed = parseInt(draft, 10);
    // Blank or garbage: back to the count, nothing sent.
    if (Number.isNaN(typed)) {
      setDraft(String(value));
      return;
    }
    // A negative is a slip, not an instruction to clear the count - putting
    // it through as 0 would wipe a recorded arrival.
    if (typed < 0) {
      setDraft(String(value));
      return;
    }
    const next = typed;
    // Over the cap: put the old number back and say what is in the way. It
    // used to clamp to the cap instead, which turned a refusal into a number
    // the buyer never typed - and when the cap was 0 it did nothing at all,
    // with no toast, no request and no explanation.
    if (next > max) {
      setDraft(String(value));
      onTooHigh?.(next);
      return;
    }
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  if (readOnly) {
    return (
      <Typography sx={{ ...CELL_PRIMARY, color: complete ? "success.main" : undefined }}>
        {value} / {total}
      </Typography>
    );
  }

  return (
    <TextField
      size="small"
      type="number"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        // Escape abandons the edit, and must not reach the dialog behind -
        // MUI closes a Dialog on a bubbled Escape, so without this the whole
        // inquiry shuts on a cancelled keystroke.
        if (e.key === "Escape") {
          e.stopPropagation();
          setDraft(String(value));
        }
      }}
      onFocus={(e) => e.target.select()}
      inputProps={{
        min: 0,
        max,
        "aria-label": label,
        style: {
          textAlign: "right",
          width: "2.5em",
          padding: "3px 4px",
          fontSize: ROW_FONT_PRIMARY,
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" sx={{ ml: 0.25 }}>
            <Typography
              sx={{ fontSize: ROW_FONT_PRIMARY, color: "var(--black75)" }}
            >
              / {total}
            </Typography>
          </InputAdornment>
        ),
        sx: { pr: 1 },
      }}
      // Green once the line is in: the cue to run QC and mark the inquiry
      // Ready to use, since nothing moves it there automatically.
      sx={{
        width: "6.5em",
        ...(complete ? { "& input": { color: "success.main" } } : {}),
      }}
    />
  );
};
